import { useEffect, useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Mail,
  Eye,
  MousePointer,
  MailX,
  Users,
  Calendar,
  Download,
  Filter,
  Clock,
  MapPin,
  Smartphone,
  Monitor,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Calendar as CalendarIcon,
  PieChart as PieChartIcon,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchLaunchedDetailPerDay } from "../Redux/ReduxSlices/analyticsDashboardSlice";
import moment from "moment-timezone";

// Mock analytics data

const industryBreakdown = [
  {
    name: "Technology",
    value: 35,
    color: "url(#techGradient)",
    solidColor: "#3B82F6",
  },
  {
    name: "Healthcare",
    value: 25,
    color: "url(#healthGradient)",
    solidColor: "#10B981",
  },
  {
    name: "Finance",
    value: 20,
    color: "url(#financeGradient)",
    solidColor: "#F59E0B",
  },
  {
    name: "Retail",
    value: 12,
    color: "url(#retailGradient)",
    solidColor: "#EF4444",
  },
  {
    name: "Other",
    value: 8,
    color: "url(#otherGradient)",
    solidColor: "#8B5CF6",
  },
];

const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case "active":
      return "bg-green-100 text-green-800 hover:bg-green-200 hover:text-green-900";
    case "completed":
      return "bg-blue-100 text-blue-800 hover:bg-blue-200 hover:text-blue-900";
    case "sent":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 hover:text-yellow-900";
    case "success":
      return "bg-green-100 text-green-800 hover:bg-green-200 hover:text-green-900";
    case "failed":
      return "bg-red-100 text-red-800 hover:bg-red-200 hover:text-red-900";
    case "queued":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 hover:text-yellow-900";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-200 hover:text-gray-900";
  }
};

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = "text-black",
}) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex md:items-center  justify-between">
        <div>
          <p className="text-sm font-medium text-black">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-black mt-1">{subtitle}</p>}
        </div>
        <Icon className="h-8 w-8 max-md:h-6 max-md:w-6 text-black" />
      </div>
    </CardContent>
  </Card>
);

export default function AnalyticsPage() {
  const [timeRange, setTimeRange] = useState("6m");
  const [selectedTab, setSelectedTab] = useState("overview");
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector(
    (state) => state.campaignAnalytics,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!data || data.length === 0) {
      dispatch(fetchLaunchedDetailPerDay());
    }
  }, [dispatch, data]);

  const series = data?.data?.series || [];

  const campaignPerformance = series.map((item) => ({
    name: item.date, // X-axis
    sent: item.totals.sent || 0,
    failed: item.totals.failed || 0,
    queued: item.totals.queued || 0,
    attempted: item.totals.attempted || 0,
    campaigns: item.totals.campaigns || 0,
    recipients: item.totals.recipients || 0,
    success_rate: item.totals.success_rate ?? 0,
  }));
  const totals = series.reduce(
    (acc, day) => {
      acc.campaigns += day.totals.campaigns || 0;
      acc.recipients += day.totals.recipients || 0;
      acc.sent += day.totals.sent || 0;
      acc.attempted += day.totals.attempted || 0;
      acc.failed += day.totals.failed || 0;
      return acc;
    },
    { campaigns: 0, recipients: 0, sent: 0, attempted: 0, failed: 0 },
  );

  // prepare stats for UI
  const campaignStats = {
    totalCampaigns: totals.campaigns,
    totalRecipients: totals.recipients,
    successRate: totals.attempted
      ? ((totals.sent / totals.attempted) * 100).toFixed(1)
      : 0,
    failedRate: totals.attempted
      ? ((totals.failed / totals.attempted) * 100).toFixed(1)
      : 0,
  };
  const allItems =
    series.flatMap((day) =>
      (day.items || []).map((item) => ({
        ...item,
        bucket_date: day.date, // ✅ add this
      })),
    ) || [];

  const allRecipients = series.flatMap((day) =>
    day.items.flatMap((item) =>
      item.recipients_sample.map((email) => ({
        email,
        campaignId: item.campaign_id,
        campaignName: item.campaign_name,
        createdAt: item.created_at,
        status: item.sent > 0 ? "sent" : item.failed > 0 ? "failed" : "queued",
        sent: item.sent,
        failed: item.failed,
        attempted: item.attempted,
        successRate: item.success_rate,
      })),
    ),
  );


  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="container mx-auto max-md:py-2 px-12 max-md:px-4 ">
        <div className="mb-6 max-md:mb-2">
          <div className="flex max-md:flex-col items-center justify-between  rounded-2xl pt-6 max-md:pt-2 max-md:p-0 border border-white/20">
            <div>
              <h4 className="">Analytics Dashboard</h4>
              <p className="text-black max-md:text-sm mb-1 ">
                Track your campaign performance and engagement metrics
              </p>
            </div>
          </div>
        </div>

        <Tabs
          value={selectedTab}
          onValueChange={setSelectedTab}
          className="space-y-4"
        >
          <TabsList className="grid w-full  grid-cols-2 bg-white/80 backdrop-blur-sm px-3   rounded-md shadow-lg border max-md:h-10 border-black/20">
            <TabsTrigger
              value="overview"
              className="max-md:text-xs  font-bold max-md:px-2   data-[state=active]:bg-gradient-to-r from-blue-600 to-indigo-600 data-[state=active]:text-white"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="campaigns"
              className="max-md:text-xs font-bold max-md:px-2  data-[state=active]:bg-gradient-to-r from-blue-600 to-indigo-600 data-[state=active]:text-white"
            >
              Campaigns
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Campaigns"
                value={campaignStats.totalCampaigns}
                icon={Mail}
                trend="up"
              />
              <StatCard
                title="Sent Mails"
                value={campaignStats.totalRecipients.toLocaleString()}
                icon={Users}
                trend="up"
              />
              <StatCard
                title="Success Rate"
                value={`${campaignStats.successRate}%`}
                icon={Eye}
                trend="up"
              />
              <StatCard
                title="Failed Rate"
                value={`${campaignStats.failedRate}%`}
                icon={MailX}
                trend="down"
              />
            </div>

            <Card className="bg-white/90 backdrop-blur-sm border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 mb-12 max-md:mb-8">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-semibold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                  Campaign Performance Trends
                </CardTitle>
              </CardHeader>

              <CardContent className="p-0">
                {/* Scroll wrapper for mobile */}
                <div className="w-full overflow-x-auto">
                  {/* Set min width so chart can scroll on mobile */}
                  <div className="min-w-[700px] sm:min-w-full">
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart
                        data={campaignPerformance}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="attempted" fill="#6366F1" />
                        <Bar dataKey="campaigns" fill="#0EA5E9" />
                        <Bar dataKey="failed" fill="#EF4444" />
                        <Bar dataKey="queued" fill="#F59E0B" />
                        <Bar dataKey="recipients" fill="#10B981" />
                        <Bar dataKey="sent" fill="#3B82F6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="campaigns" className="space-y-6">
            <Card className="bg-white/90 backdrop-blur-sm border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 mb-12 max-md:mb-8">
              <CardHeader className="pb-4 max-md:pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-semibold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                    Recent Campaigns
                  </CardTitle>
                </div>
              </CardHeader>

              {allItems?.length === 0 ? (
                <div className="flex justify-center items-center mb-8 flex-col">
                  <Mail className="h-8 w-8 text-muted-foreground " />
                  <h3 className="text-lg font-medium mb-1 text-center">
                    No campaigns found
                  </h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    You don't have any campaigns yet
                  </p>
                </div>
              ) : (
                <CardContent className="">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 max-md:text-sm px-4 font-medium text-gray-600">
                            Campaign
                          </th>
                          {/* <th className="text-left py-3 max-md:text-sm px-4 font-medium text-gray-600">
                            Status
                          </th> */}
                          <th className="text-left py-3 max-md:text-sm px-4 font-medium text-gray-600">
                            Recipients
                          </th>
                          <th className="text-left py-3 max-md:text-sm px-4 font-medium text-gray-600">
                            Success Rate
                          </th>
                          <th className="text-left py-3 max-md:text-sm px-4 font-medium text-gray-600">
                            Sent Rate
                          </th>
                          <th className="text-left py-3 max-md:text-sm px-4 font-medium text-gray-600">
                            Failed Rate
                          </th>
                          <th className="text-left py-3 max-md:text-sm px-4 font-medium text-gray-600">
                            Subject of Email
                          </th>

                          <th className="text-left py-3 max-md:text-sm px-4 font-medium text-gray-600">
                            Last Activity
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {allItems.reverse().map((campaign, index) => (
                          <tr
                            key={campaign.campaign_id || index}
                            className="border-b hover:bg-gray-50"
                          >
                            {/* Campaign Name */}
                            <td className="py-3 px-4 font-medium max-md:text-sm">
                              {campaign.campaign_name
                                ? campaign.campaign_name
                                    .charAt(0)
                                    .toUpperCase() +
                                  campaign.campaign_name.slice(1)
                                : "" || "Unnamed Campaign"}
                            </td>

                            {/* Status → based on success/failed */}
                            {/* <td className="py-3 px-4 max-md:text-sm">
                              <Badge
                                className={getStatusColor(
                                  campaign.success_rate > 0.5
                                    ? "Success"
                                    : "Failed",
                                )}
                              >
                                {campaign.success_rate == 1
                                  ? "Success"
                                  : "Failed"}
                              </Badge>
                            </td> */}

                            {/* Recipients */}
                            <td className="py-3 px-4 max-md:text-sm">
                              {campaign.recipients_count}
                            </td>

                            {/* Success Rate */}
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="font-medium max-md:text-sm">
                                  {(campaign.success_rate * 100).toFixed(1)}%
                                </span>
                                <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="bg-green-600 h-1.5 rounded-full"
                                    style={{
                                      width: `${campaign.success_rate * 100}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </td>

                            {/* Failed */}
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="font-medium max-md:text-sm">
                                  {campaign.sent}
                                </span>
                              </div>
                            </td>

                            {/* Failed */}
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="font-medium max-md:text-sm">
                                  {campaign.failed}
                                </span>
                              </div>
                            </td>

                            {/* Subject */}
                            <td className="py-3 px-4 max-md:text-xs">
                              {campaign.template_subject
                                ? campaign.template_subject
                                : campaign.subject_snapshot}
                            </td>

                            {/* Last Activity */}
                            <td className="py-3  text-gray-600 ">
                              <div className="flex items-center gap-1 text-wrap text-xs">
                                <Clock className="h-3 w-3" />
                                {/* {formatDate(campaign.bucket_date + "Z")} */}
                                {moment(campaign.created_at).format(
                                  "DD MMM YYYY, hh:mm a",
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <div className="pt-8">
        <Footer />
      </div>
    </div>
  );
}
